import React, { useState } from "react";
import { Box, MenuItem, Modal, Switch, TextField } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Close from "../../Icons/Close";
import Button from "../../../Subcomponents/Button";
import { errorTost } from "../../../Services/Toast";
import {
  ASK_FOR_OPTIONS,
  NAME_CHECK_OPTIONS,
  PLACEHOLDERS,
  normaliseEsignRules,
} from "./esignRules";
import cssClasses from "./CustomerConsentSettings.module.css";

// Where each consent is asked in the Customer Portal
export const CONSENT_TYPES = [
  { type: "token", label: "Token Payment Consent", help: "Asked before the customer's first token payment." },
  { type: "allotment", label: "Allotment Payment Consent", help: "Asked before the customer's first allotment payment." },
  {
    type: "virtual_account",
    label: "Virtual Account Consent",
    help: "Asked before the customer can see their virtual account details.",
  },
];

const EDITOR_TOOLBAR = ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "blockQuote", "undo", "redo"];

const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

export default function AddConsentModal({ open, consent, availableTypes, saving, handleClose, onSave }) {
  const isEdit = !!consent;
  const [type, setType] = useState(consent?.type || availableTypes[0]?.type || "");
  const [title, setTitle] = useState(consent?.title || availableTypes[0]?.label || "");
  const [content, setContent] = useState(consent?.content || "");
  const [esign, setEsign] = useState(() => normaliseEsignRules(consent?.esignRules));
  const setEsignField = (key) => (e) => setEsign((prev) => ({ ...prev, [key]: e.target.value }));

  function save() {
    if (!type) return errorTost("Please select a consent type");
    if (!title.trim()) return errorTost("Please enter a title");
    if (!stripHtml(content)) return errorTost("Please enter the consent text");
    const payload = { type, title: title.trim(), content };
    if (type === "allotment") payload.esignRules = normaliseEsignRules(esign);
    onSave(payload);
  }

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }}
      disableAutoFocus
      disableEnforceFocus // CKEditor link balloon needs focus outside the modal
    >
      <Box className={cssClasses.modalWrapper}>
        <div className={cssClasses.modalHeader}>
          <span>{isEdit ? "Edit Consent" : "Add Consent"}</span>
          <span className={cssClasses.modalClose} onClick={handleClose} tabIndex={0}>
            <Close width={"1.2rem"} height={"1.2rem"} />
          </span>
        </div>

        <div className={cssClasses.modalContent}>
          <div className={cssClasses.fieldLabel}>
            Consent For <span style={{ color: "red" }}>*</span>
          </div>
          <TextField
            select
            fullWidth
            size="small"
            value={type}
            disabled={isEdit}
            onChange={(e) => {
              const next = e.target.value;
              // Keep the default title in step with the type until the user types their own
              if (!title || CONSENT_TYPES.some((t) => t.label === title)) {
                setTitle(CONSENT_TYPES.find((t) => t.type === next)?.label || "");
              }
              setType(next);
            }}
            SelectProps={{ MenuProps: { sx: { zIndex: 1400 } } }}
            inputProps={{ style: { fontSize: "1.4rem" } }}
          >
            {availableTypes.map((t) => (
              <MenuItem key={t.type} value={t.type} sx={{ fontSize: "1.4rem" }}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <div className={cssClasses.cardHelp}>{CONSENT_TYPES.find((t) => t.type === type)?.help}</div>

          <div className={cssClasses.fieldLabel}>
            Title <span style={{ color: "red" }}>*</span>
          </div>
          <TextField
            fullWidth
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Shown as the popup heading"
            inputProps={{ style: { fontSize: "1.4rem" } }}
          />

          <div className={cssClasses.fieldLabel}>
            Consent Text <span style={{ color: "red" }}>*</span>
          </div>
          <div className={cssClasses.editor}>
            <CKEditor
              editor={ClassicEditor}
              config={{ toolbar: EDITOR_TOOLBAR }}
              data={content}
              onChange={(event, editor) => setContent(editor.getData())}
            />
          </div>

          {type === "allotment" && (
            <>
              <div className={cssClasses.placeholderHelp}>
                <div className={cssClasses.placeholderTitle}>Placeholders you can use in the consent text</div>
                {PLACEHOLDERS.map((p) => (
                  <div key={p.tag} className={cssClasses.placeholderRow}>
                    <code>{p.tag}</code> {p.help}
                  </div>
                ))}
              </div>

              <div className={cssClasses.esignHead}>
                <span className={cssClasses.fieldLabelInline}>Require Aadhaar eSign</span>
                <Switch
                  checked={esign.enabled}
                  onChange={(e) => setEsign((prev) => ({ ...prev, enabled: e.target.checked }))}
                  inputProps={{ "aria-label": "Require Aadhaar eSign" }}
                />
              </div>
              <div className={cssClasses.cardHelp}>
                Preselection: after filling KYC, the customer signs these terms with Aadhaar eSign before paying.
              </div>
              {esign.enabled && (
                <>
                  {[
                    { key: "askFor", label: "Ask for", options: ASK_FOR_OPTIONS },
                    { key: "nameCheck", label: "Aadhaar name check", options: NAME_CHECK_OPTIONS },
                  ].map((f) => (
                    <React.Fragment key={f.key}>
                      <div className={cssClasses.fieldLabel}>{f.label}</div>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={esign[f.key]}
                        onChange={setEsignField(f.key)}
                        SelectProps={{ MenuProps: { sx: { zIndex: 1400 } } }}
                        inputProps={{ style: { fontSize: "1.4rem" } }}
                      >
                        {f.options.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: "1.4rem" }}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </React.Fragment>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div className={cssClasses.modalFooter}>
          <Button
            style={{ width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem" }}
            btnName={"Cancel"}
            clicked={handleClose}
          />
          <Button
            style={{ width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", fontSize: "1.4rem", lineHeight: "2rem" }}
            btnName={isEdit ? "Update" : "Save"}
            disable={saving}
            isLoading={saving}
            clicked={save}
          />
        </div>
      </Box>
    </Modal>
  );
}
